import React from 'react';
import { motion } from 'framer-motion';
import { doctors } from '../data/doctors';
import DoctorCard from '../components/DoctorCard';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
  defaultTransition,
} from '../utils/animations';

export default function About() {
  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-16 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Orbs - Fewer and more subtle than Hero */}
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {/* Light Particles - Fewer than Hero */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.h1
            className="text-4xl font-bold text-gray-900 mb-4"
            variants={fadeInUp}
          >
            Echipa Noastră
          </motion.h1>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            O echipă de profesioniști dedicați, cu experiență vastă în
            oncologie, gata să ofere cele mai bune soluții pentru pacienții
            noștri.
          </motion.p>
        </motion.div>

        {/* Doctor Cards Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-50px' }}
          variants={fastStaggerContainer}
        >
          {doctors.map((doctor) => (
            <motion.div
              key={doctor.id}
              variants={scaleIn}
              whileHover={{
                y: -8,
                transition: { duration: 0.3 },
              }}
            >
              <DoctorCard doctor={doctor} />
            </motion.div>
          ))}
        </motion.div>

        {/* Mission Section */}
        <motion.div
          className="mt-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          <motion.div
            className="relative bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-8 border border-gray-100"
            variants={scaleIn}
            whileHover={{
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transition: { duration: 0.3 },
            }}
          >
            <motion.h2
              className="text-2xl font-bold text-gray-900 mb-4"
              variants={fadeInUp}
            >
              Misiunea Noastră
            </motion.h2>
            <motion.p
              className="text-gray-600 leading-relaxed"
              variants={fadeInUp}
            >
              La Decizie Oncologică, ne-am asumat misiunea de a oferi cea mai
              bună îngrijire oncologică posibilă, combinând expertiza medicală
              cu empatie și suport personal. Echipa noastră multidisciplinară
              colaborează pentru a dezvolta planuri de tratament personalizate,
              utilizând cele mai recente progrese în domeniul oncologiei.
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
